import { SalonData, ServiceCategory, TimeSlot } from '../types/salonTypes';
import apiService from '../utils/api';

// Randevu slotları için tip tanımı
interface AppointmentSlot {
  startTime: string;
  endTime: string;
}

// Çalışma saati tipi
interface WorkingHoursSlot {
  startTime: string;
  endTime: string;
}

export const generateTimeSlots = async (salonId: string, staffId: string, date: Date): Promise<TimeSlot[]> => {
  try {
    // Tarihi YYYY-MM-DD formatına çevir
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    // Randevuları API'den al
    const response = await apiService.getAppointments(salonId, staffId, dateString);
    
    if (!response.success) {
      console.error('Randevu verileri alınamadı:', response.message);
      return [];
    }
    
    // Çalışma saatleri - artık backend'den geliyor
    const workingHours: WorkingHoursSlot[] = response.workingHours || [];
    
    // Çalışma saati yoksa boş dizi döndür
    if (workingHours.length === 0) {
      console.error('Çalışma saati bulunamadı');
      return [];
    }
    
    // API'den çalışma saatlerini kullan
    // Yoksa, varsayılan olarak 9-20 arası
    const defaultStartHour = 9;
    const defaultEndHour = 20;
    
    const slots: TimeSlot[] = [];
    const bookedSlots: AppointmentSlot[] = response.appointments || [];
    
    // Her çalışma saati aralığı için slotları oluştur
    for (const workingHour of workingHours) {
      // Başlangıç ve bitiş saatlerini ayrıştır
      const [startHour, startMinute] = workingHour.startTime.split(':').map(Number);
      const [endHour, endMinute] = workingHour.endTime.split(':').map(Number);
      
      console.log(`Çalışma saati: ${workingHour.startTime} - ${workingHour.endTime}`);
      
      // Başlangıç saatini yuvarla (aşağı)
      let currentHour = startHour;
      let currentMinute = Math.floor(startMinute / 15) * 15;
      
      // Başlangıç ve bitiş saatlerini dakika cinsinden hesapla (karşılaştırma için)
      const startTotalMinutes = startHour * 60 + startMinute;
      const endTotalMinutes = endHour * 60 + endMinute;
      
      // Çalışma saati boyunca 15 dakikalık slotlar oluştur
      while (currentHour * 60 + currentMinute < endTotalMinutes) {
        const timeString = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
        
        // Bu saat dilimi dolu mu kontrol et
        const isBooked = bookedSlots.some((slot: AppointmentSlot) => {
          // Randevu başlangıç ve bitiş saatleri arasında mı?
          const slotStartTime = slot.startTime.split(':').map(Number);
          const slotEndTime = slot.endTime.split(':').map(Number);
          
          const slotStartMinutes = currentHour * 60 + currentMinute;
          const appointmentStartMinutes = slotStartTime[0] * 60 + slotStartTime[1];
          const appointmentEndMinutes = slotEndTime[0] * 60 + slotEndTime[1];
          
          return slotStartMinutes >= appointmentStartMinutes && slotStartMinutes < appointmentEndMinutes;
        });
        
        slots.push({
          id: `${currentHour}-${currentMinute}`,
          time: timeString,
          available: !isBooked,
        });
        
        // 15 dakika ekle
        currentMinute += 15;
        if (currentMinute >= 60) {
          currentHour += 1;
          currentMinute = 0;
        }
      }
    }
    
    return slots;
  } catch (error) {
    console.error('Zaman slotları oluşturulurken hata:', error);
    return [];
  }
};

const salonData: SalonData = {
  id: 'salon1',
  name: 'Bella Güzellik Salonu',
  slogan: 'Güzellik ve Bakım Uzmanınız',
  description: 'İstanbul\'un kalbinde, en yeni teknolojiler ve uzman kadromuzla hizmetinizdeyiz. 2010 yılından beri müşterilerimize kaliteli hizmet sunmaktayız.',
  logoUrl: '/images/logo.png',
  coverImages: [
    '/images/salon1.jpg',
    '/images/salon2.jpg',
    '/images/salon3.jpg',
  ],
  galleryImages: [
    {
      original: '/images/gallery1.jpg',
      thumbnail: '/images/gallery1-thumb.jpg',
      description: 'Modern salonumuzdan bir görünüm',
    },
    {
      original: '/images/gallery2.jpg',
      thumbnail: '/images/gallery2-thumb.jpg',
      description: 'Profesyonel ekipmanlarımız',
    },
    {
      original: '/images/gallery3.jpg',
      thumbnail: '/images/gallery3-thumb.jpg',
      description: 'Ferah bekleme alanımız',
    },
    {
      original: '/images/gallery4.jpg',
      thumbnail: '/images/gallery4-thumb.jpg',
      description: 'Manikür ve pedikür bölümü',
    },
    {
      original: '/images/gallery5.jpg',
      thumbnail: '/images/gallery5-thumb.jpg',
      description: 'Cilt bakım odamız',
    },
  ],
  contact: {
    address: 'Bağdat Caddesi No:123, Kadıköy, İstanbul',
    phone: '+90 212 345 67 89',
    email: 'info@bellaguzellik.com',
    whatsapp: '+90 533 123 45 67',
    instagram: 'bella.guzellik',
    facebook: 'BellaGuzellikSalonu',
    workingHours: {
      monday: '09:00 - 20:00',
      tuesday: '09:00 - 20:00',
      wednesday: '09:00 - 20:00',
      thursday: '09:00 - 20:00',
      friday: '09:00 - 20:00',
      saturday: '10:00 - 18:00',
      sunday: 'Kapalı',
    },
    mapLocation: {
      lat: 40.9824,
      lng: 29.0295,
      zoom: 15,
    },
  },
  serviceCategories: [
    {
      id: 'cat1',
      name: 'Saç Hizmetleri',
      icon: 'content_cut',
      services: [
        {
          id: 'serv1',
          name: 'Saç Kesimi',
          description: 'Profesyonel saç kesimi ve şekillendirme',
          price: 150,
          duration: 45, // dakika
          image: '/images/services/haircut.jpg',
        },
        {
          id: 'serv2',
          name: 'Saç Boyama',
          description: 'Tek renk profesyonel saç boyama',
          price: 350,
          duration: 120,
          image: '/images/services/haircoloring.jpg',
        },
        {
          id: 'serv3',
          name: 'Fön',
          description: 'Saç yıkama ve fön çekimi',
          price: 120,
          duration: 30,
          image: '/images/services/blowdry.jpg',
        },
        {
          id: 'serv4',
          name: 'Kaynak / Keratin Bakımı',
          description: 'Saç sağlığını yenileyen keratin bakım uygulaması',
          price: 800,
          duration: 150,
          image: '/images/services/keratin.jpg',
        },
      ],
    },
    {
      id: 'cat2',
      name: 'Tırnak Hizmetleri',
      icon: 'spa',
      services: [
        {
          id: 'serv5',
          name: 'Manikür',
          description: 'Klasik el bakımı ve oje uygulaması',
          price: 100,
          duration: 45,
          image: '/images/services/manicure.jpg',
        },
        {
          id: 'serv6',
          name: 'Pedikür',
          description: 'Kapsamlı ayak bakımı ve oje uygulaması',
          price: 120,
          duration: 50,
          image: '/images/services/pedicure.jpg',
        },
        {
          id: 'serv7',
          name: 'Kalıcı Oje',
          description: 'Uzun süre kalıcı ve parlak oje uygulaması',
          price: 180,
          duration: 60,
          image: '/images/services/gelpolish.jpg',
        },
      ],
    },
    {
      id: 'cat3',
      name: 'Cilt Bakımı',
      icon: 'face',
      services: [
        {
          id: 'serv8',
          name: 'Klasik Cilt Bakımı',
          description: 'Temizleme, peeling ve maske uygulaması',
          price: 250,
          duration: 60,
          image: '/images/services/facial.jpg',
        },
        {
          id: 'serv9',
          name: 'Anti-Aging Bakım',
          description: 'Kırışıklık karşıtı özel bakım',
          price: 400,
          duration: 75,
          image: '/images/services/antiaging.jpg',
        },
        {
          id: 'serv10',
          name: 'Hydrafacial',
          description: 'Cildi derinlemesine temizleyen ve nemlendiren uygulama',
          price: 500,
          duration: 60,
          image: '/images/services/hydrafacial.jpg',
        },
      ],
    },
    {
      id: 'cat4',
      name: 'Makyaj',
      icon: 'brush',
      services: [
        {
          id: 'serv11',
          name: 'Gündüz Makyajı',
          description: 'Doğal ve günlük kullanıma uygun makyaj',
          price: 200,
          duration: 45,
          image: '/images/services/daymakeup.jpg',
        },
        {
          id: 'serv12',
          name: 'Gece Makyajı',
          description: 'Özel günler için göz alıcı makyaj',
          price: 300,
          duration: 60,
          image: '/images/services/eveningmakeup.jpg',
        },
        {
          id: 'serv13',
          name: 'Gelin Makyajı',
          description: 'Düğün günü için özel makyaj uygulaması',
          price: 600,
          duration: 90,
          image: '/images/services/bridalmakeup.jpg',
        },
      ],
    },
    {
      id: 'cat5',
      name: 'Epilasyon',
      icon: 'waves',
      services: [
        {
          id: 'serv14',
          name: 'Yüz Epilasyonu',
          description: 'Lazer teknolojisi ile yüz bölgesi epilasyonu',
          price: 300,
          duration: 30,
          image: '/images/services/facelaser.jpg',
        },
        {
          id: 'serv15',
          name: 'Bacak Epilasyonu',
          description: 'Tam bacak lazer epilasyon',
          price: 800,
          duration: 60,
          image: '/images/services/leglaser.jpg',
        },
        {
          id: 'serv16',
          name: 'Kol Epilasyonu',
          description: 'Tam kol lazer epilasyon',
          price: 600,
          duration: 45,
          image: '/images/services/armlaser.jpg',
        },
      ],
    },
  ],
  testimonials: [
    {
      id: 'test1',
      name: 'Ayşe Yılmaz',
      rating: 5,
      comment: 'Harika bir deneyimdi, saç boyamam tam istediğim gibi oldu. Kesinlikle tekrar geleceğim!',
      date: '2023-06-15',
      service: 'Saç Boyama',
    },
    {
      id: 'test2',
      name: 'Mehmet Kaya',
      rating: 4,
      comment: 'Personel çok ilgili ve nazikti. Salonda beklerken kahve ikramı da çok hoştu.',
      date: '2023-05-22',
      service: 'Saç Kesimi',
    },
    {
      id: 'test3',
      name: 'Zeynep Aydın',
      rating: 5,
      comment: 'Kalıcı oje uygulaması 3 haftadır hiç bozulmadan duruyor. Çok memnun kaldım.',
      date: '2023-07-01',
      service: 'Kalıcı Oje',
    },
    {
      id: 'test4',
      name: 'Can Demir',
      rating: 5,
      comment: 'Cilt bakımı sonrası cildim çok daha parlak ve canlı görünüyor. Teşekkürler!',
      date: '2023-06-28',
      service: 'Klasik Cilt Bakımı',
    },
  ],
  team: [
    {
      id: 'staff1',
      name: 'Seda Yıldız',
      position: 'Saç Stilisti & Salon Sahibi',
      bio: '15 yıllık deneyimi ile saç kesimi ve renklendirme konusunda uzman.',
      image: '/images/team/stylist1.jpg',
      specialties: ['Saç Kesimi', 'Saç Boyama', 'Keratin Bakımı'],
    },
    {
      id: 'staff2',
      name: 'Deniz Acar',
      position: 'Tırnak Teknisyeni',
      bio: 'Özel tasarım tırnak uygulamaları ve kalıcı oje konusunda sertifikalı uzman.',
      image: '/images/team/nailtech.jpg',
      specialties: ['Manikür', 'Pedikür', 'Kalıcı Oje'],
    },
    {
      id: 'staff3',
      name: 'Emine Kara',
      position: 'Cilt Bakım Uzmanı',
      bio: 'Medikal cilt bakımı ve anti-aging uygulamaları konusunda 8 yıllık deneyim.',
      image: '/images/team/skincare.jpg',
      specialties: ['Klasik Cilt Bakımı', 'Anti-Aging Bakım', 'Hydrafacial'],
    },
    {
      id: 'staff4',
      name: 'Burak Öztürk',
      position: 'Makyaj Artisti',
      bio: 'Ulusal yarışmalarda ödül almış profesyonel makyaj sanatçısı.',
      image: '/images/team/makeup.jpg',
      specialties: ['Gündüz Makyajı', 'Gece Makyajı', 'Gelin Makyajı'],
    },
  ],
  faqs: [
    {
      id: 'faq1',
      question: 'Randevu iptal etmek istersem ne yapmalıyım?',
      answer: 'Randevunuzu 24 saat öncesine kadar ücretsiz olarak iptal edebilirsiniz. İptal işlemi için bizi telefonla arayabilir veya çevrimiçi randevu sistemi üzerinden iptal edebilirsiniz.',
    },
    {
      id: 'faq2',
      question: 'Hangi ödeme yöntemlerini kabul ediyorsunuz?',
      answer: 'Nakit, kredi kartı, banka kartı ve mobil ödeme yöntemlerini kabul ediyoruz. Ayrıca salon içi uygulamalarımızda taksit imkanı da bulunmaktadır.',
    },
    {
      id: 'faq3',
      question: 'Hizmetlerinizde kullandığınız ürünler hakkında bilgi alabilir miyim?',
      answer: 'Salonumuzda dünya çapında tanınmış profesyonel markaların ürünlerini kullanıyoruz. Kullanılan ürünler hakkında detaylı bilgiyi uzmanlarımızdan alabilirsiniz.',
    },
    {
      id: 'faq4',
      question: 'Hediye kartı satın alabilir miyim?',
      answer: 'Evet, sevdikleriniz için salon hizmetlerimizi kapsayan hediye kartları satın alabilirsiniz. Hediye kartları fiziksel olarak salonumuzdan veya çevrimiçi web sitemiz üzerinden dijital olarak temin edilebilir.',
    },
    {
      id: 'faq5',
      question: 'İlk ziyaretimde nelere dikkat etmeliyim?',
      answer: 'İlk ziyaretinizde randevunuzdan 10 dakika önce gelmenizi öneririz. Bu süre kayıt işlemleriniz ve isteklerinizi detaylı konuşmak için zaman sağlayacaktır. Ayrıca herhangi bir alerjiniz veya sağlık sorununuz varsa uzmanımızı bilgilendirmeniz önemlidir.',
    },
  ],
};

export default salonData; 